import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom"; 
import { toast, ToastContainer } from "react-toastify";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Spinner,
} from "reactstrap";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFutbol,
  faListOl,
  faBroadcastTower,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import TournamentsPage from "./TournamentsPage";
import MatchesPage from "./MatchesPage";
import LiveMatchesPage from "./LiveMatchesPage";
import footballService from "../../services/api/football-service.js";

const FootballPage = () => {
  const location = useLocation();
  const history = useHistory();
  const tabNameToId = { tournaments: "1", matches: "2", live: "3" };
  const tabIdToName = { 1: "tournaments", 2: "matches", 3: "live" };
  
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const initialTabName = queryParams.get("tab") || "tournaments";
  const initialTabId = tabNameToId[initialTabName] || "1";
  const [activeTab, setActiveTab] = useState(initialTabId);
  const [isSyncing, setIsSyncing] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    const syncStartTime = sessionStorage.getItem('footballSyncStartTime');
    if (syncStartTime) {
      const fiveMinutes = 5 * 60 * 1000;
      if (Date.now() - parseInt(syncStartTime, 10) > fiveMinutes) {
        sessionStorage.removeItem('footballSyncStartTime');
        setIsSyncing(false);
      } else {
        setIsSyncing(true);
      }
    }
  }, []);

  useEffect(() => {
    const tabNameFromUrl = queryParams.get("tab") || "tournaments";
    const tabIdFromUrl = tabNameToId[tabNameFromUrl] || "1";
    if (tabIdFromUrl !== activeTab) {
      setActiveTab(tabIdFromUrl);
    }
  }, [location.search, activeTab]);

  const toggleTab = (tabId) => {
    const tabName = tabIdToName[tabId];
    history.push(`/dashboard/football/data-center?tab=${tabName}`);
  };

 const handleResync = async () => {
    sessionStorage.setItem('footballSyncStartTime', Date.now().toString());
    setIsSyncing(true);
    let wasSuccessful = false;

    try {
      await footballService.resyncData();
      wasSuccessful = true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Resync failed";
      toast.error(`Resync failed: ${errorMessage}`);
      wasSuccessful = false;
    } finally {
      sessionStorage.removeItem('footballSyncStartTime');
      setIsSyncing(false);
      if (wasSuccessful) {
        toast.success("Resync Completed Successfully!");
        setRefetchTrigger((prev) => prev + 1);
      }
    }
  };
  
  const tslug = queryParams.get("tslug") || null;
  const roundSlug = queryParams.get("roundSlug") || null;

  return (
    <div className="content py-4">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <style>{`
        .modern-tab { padding: 12px 20px; cursor: pointer; background: none; border: none; color: #6c757d; font-weight: 600; font-size: 1rem; transition: color 0.2s ease, border-bottom 0.2s ease; border-bottom: 3px solid transparent; }
        .modern-tab.active { color: #0d6efd; border-bottom: 3px solid #0d6efd; }
      `}</style>
      <Container fluid>
        <Row>
          <Col md="12">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="fw-bold mb-0">⚽ Football Info </h2>
              <div>
                {(activeTab === "1" || activeTab === "2") && (
                  <Button
                    color="success"
                    onClick={handleResync}
                    disabled={isSyncing}
                  >
                    {isSyncing ? (
                      <Spinner size="sm" />
                    ) : (
                      <FontAwesomeIcon icon={faSync} />
                    )}
                    <span className="ms-2">
                      {isSyncing ? "Syncing..." : "Resync Data"}
                    </span>
                  </Button>
                )}
              </div>
            </div>
            <Card className="shadow-sm border-0">
              <CardHeader className="bg-white border-0 py-0 px-3">
                <div className="d-flex border-bottom">
                  <button
                    className={classnames("modern-tab", { active: activeTab === "1" })}
                    onClick={() => toggleTab("1")}
                  >
                    <FontAwesomeIcon icon={faFutbol} className="me-2" /> Tournaments
                  </button>
                  <button
                    className={classnames("modern-tab", { active: activeTab === "2" })}
                    onClick={() => toggleTab("2")}
                  >
                    <FontAwesomeIcon icon={faListOl} className="me-2" /> All Matches
                  </button>
                  <button
                    className={classnames("modern-tab", { active: activeTab === "3" })}
                    onClick={() => toggleTab("3")}
                  >
                    <FontAwesomeIcon icon={faBroadcastTower} className="me-2" /> Live
                  </button>
                </div>
              </CardHeader>
              <CardBody className="p-0">
                {activeTab === "1" && (
                  <TournamentsPage refetchTrigger={refetchTrigger} />
                )}
                {activeTab === "2" && (
                  <MatchesPage
                    refetchTrigger={refetchTrigger}
                    tslug={tslug}
                    roundSlug={roundSlug}
                  />
                )}
                {activeTab === "3" && <LiveMatchesPage />}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};
export default FootballPage;